import React, { useEffect, useMemo, useRef, useState } from 'react';
import { rsi as rsiCalc } from '../../lib/crypto/indicators/math';
import { macd as macdCalc, atr as atrCalc, adx as adxCalc, rvol as rvolCalc } from '../../lib/crypto/indicators/mathB';

/**
 * IndicatorPane — one canvas pane rendered below the main chart. Uses
 * a small drawing DSL so all sub-indicators share hit-testing / grid /
 * label logic. Supports:
 *
 *   type = 'rsi' | 'macd' | 'atr' | 'adx' | 'rvol'
 *
 * Props:
 *   candles       — array of {time, open, high, low, close, volume}
 *   height        — pane pixel height
 *   visibleRange  — {from, to} in unix seconds (from main chart)
 *   engine        — ChartEngine ref from the main chart. When provided,
 *                   the pane reads the LIVE priceScale width and uses
 *                   the chart's `timeToX(t)` mapping so every bar sits
 *                   directly under its parent candle. Falls back to a
 *                   linear layout if the engine is not yet mounted.
 */
export default function IndicatorPane({ type, candles, height = 90, visibleRange = null, engine = null, crosshairTime = null, onHoverTime = null }) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const [size, setSize] = useState({ w: 400, h: height });
  // Tick counter — bump on visible-range change so the effect re-runs
  // and re-samples engine.timeToX for the new zoom / pan.
  const [rangeTick, setRangeTick] = useState(0);
  useEffect(() => { setRangeTick((t) => t + 1); }, [visibleRange]);

  // Slice candles to the chart's visible time range so the pane's
  // horizontal axis lines up bar-for-bar with the main chart.
  const localCandles = useMemo(() => {
    if (!visibleRange || !candles?.length) return candles || [];
    const from = visibleRange.from;
    const to = visibleRange.to;
    return candles.filter((c) => c.time >= from && c.time <= to);
  }, [candles, visibleRange]);

  useEffect(() => {
    if (!rootRef.current) return;
    const obs = new ResizeObserver(() => {
      if (!rootRef.current) return;
      setSize({ w: rootRef.current.clientWidth, h: height });
    });
    obs.observe(rootRef.current);
    setSize({ w: rootRef.current.clientWidth, h: height });
    return () => obs.disconnect();
  }, [height]);

  // Indicator dataset (kept independent of `localCandles` so long
  // lookback periods stay correct even when the user zooms in).
  const draw = useMemo(() => {
    // Compute over the FULL candle array — otherwise EMAs / RSI /
    // MACD would reset every time the user pans and the smoothing
    // seed would drift. The slice happens at render time.
    const src = candles || [];
    if (!src.length) return null;
    if (type === 'rsi')   return { kind: 'osc',  rows: rsiCalc(src, 14), min: 0, max: 100, bands: [30, 70], color: '#00e5ff', label: 'RSI' };
    if (type === 'atr')   return { kind: 'line', rows: atrCalc(src, 14), color: '#ff7ac6', label: 'ATR 14' };
    if (type === 'macd')  return { kind: 'macd', data: macdCalc(src, 12, 26, 9), label: 'MACD 12,26,9' };
    if (type === 'adx')   return { kind: 'adx',  data: adxCalc(src, 14), label: 'ADX 14' };
    if (type === 'rvol')  return { kind: 'rvol', rows: rvolCalc(src, 20), label: 'RVOL 20' };
    return null;
  }, [type, candles]);

  useEffect(() => {
    const cnv = canvasRef.current;
    if (!cnv || !draw) return;
    const dpr = window.devicePixelRatio || 1;
    cnv.width = size.w * dpr;
    cnv.height = size.h * dpr;
    cnv.style.width = `${size.w}px`;
    cnv.style.height = `${size.h}px`;
    const ctx = cnv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);

    // ── Layout ──
    // Read the main chart's LIVE right-price-scale width so our pane
    // reserves the exact same strip on the right (Phase 52.1 G2 #5).
    // This makes RVOL/MACD/etc bars span the full body width just
    // like the main chart, with no horizontal drift.
    const scaleWidth = (engine?.provider?.priceScaleWidth?.() ?? 78);
    const pad = { top: 12, bottom: 8, left: 0, right: Math.max(48, scaleWidth) };
    const w = size.w - pad.left - pad.right;
    const h = size.h - pad.top - pad.bottom;

    // ── X-axis mapping ──
    // If the engine exposes timeToX(), use it for every bar so bars
    // sit directly under the parent candle. Otherwise fall back to
    // linear spacing across the visible slice.
    const canT2X = typeof engine?.provider?.timeToX === 'function';
    const barsRef = draw.kind === 'macd' ? draw.data.hist
                 : draw.kind === 'rvol' ? draw.rows
                 : draw.kind === 'adx'  ? draw.data.adx
                 : draw.rows || [];
    // rangeTick is intentionally read to force re-run on zoom/pan.
    void rangeTick;
    // Build a { time -> x } snapshot for the currently-visible bars.
    // For lightweight-charts, timeToCoordinate returns null for bars
    // outside the visible range — we simply skip them.
    const xOf = (time, fallbackIdx, totalLen) => {
      if (canT2X) {
        const x = engine.provider.timeToX(time);
        if (x != null) return pad.left + x;
      }
      // Fallback: linear across the current visible slice.
      if (!totalLen || totalLen <= 1) return pad.left;
      return pad.left + (fallbackIdx / (totalLen - 1)) * w;
    };
    const barSpacing = canT2X ? (() => {
      try {
        const ts = engine.provider.chart.timeScale();
        return ts.options().barSpacing || 8;
      } catch { return 8; }
    })() : Math.max(1, w / Math.max(1, barsRef.length));

    const drawYAxis = (min, max, ticks) => {
      ctx.font = '9px "JetBrains Mono", monospace';
      ctx.fillStyle = '#94a3b8';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const axisX = size.w - 4;   // right-edge label (inside axis strip)
      for (const v of ticks) {
        const y = pad.top + h - ((v - min) / (max - min)) * h;
        ctx.fillText(String(v), axisX, y);
        // faint tick line
        ctx.strokeStyle = 'rgba(255,255,255,0.05)';
        ctx.setLineDash([2, 3]);
        ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + w, y); ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    };

    // ── Filter rows to the current visible time window ──
    const from = visibleRange?.from ?? -Infinity;
    const to   = visibleRange?.to   ?? +Infinity;
    const inRange = (r) => r.time >= from && r.time <= to;

    const renderLine = (rows, color, min, max, bands = []) => {
      // grid + bands
      for (const b of bands) {
        const y = pad.top + h - ((b - min) / (max - min)) * h;
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(255,255,255,0.10)';
        ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + w, y); ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.strokeStyle = color; ctx.lineWidth = 1.2;
      ctx.beginPath();
      const visible = rows.filter(inRange);
      visible.forEach((r, i) => {
        const x = xOf(r.time, i, visible.length);
        const y = pad.top + h - ((Math.max(min, Math.min(max, r.value)) - min) / (max - min)) * h;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
    };

    if (draw.kind === 'osc') {
      renderLine(draw.rows, draw.color, draw.min, draw.max, draw.bands);
      drawYAxis(draw.min, draw.max, [0, 30, 50, 70, 100]);
      const last = draw.rows.filter(inRange).slice(-1)[0]?.value;
      if (last != null) {
        ctx.font = '10px "JetBrains Mono", monospace';
        ctx.fillStyle = last >= draw.bands[1] ? '#ff3366' :
                        last <= draw.bands[0] ? '#00ff88' : draw.color;
        ctx.fillText(`${draw.label} ${last.toFixed(1)}`, pad.left + 4, pad.top + 10);
      }
      return;
    }

    if (draw.kind === 'line') {
      const rows = draw.rows;
      if (!rows.length) return;
      const visible = rows.filter(inRange);
      if (!visible.length) return;
      const min = Math.min(...visible.map((r) => r.value));
      const max = Math.max(...visible.map((r) => r.value));
      renderLine(rows, draw.color, min, max);
      const step = (max - min) / 4;
      drawYAxis(min, max, [min, min + step, min + 2 * step, min + 3 * step, max].map((v) => +v.toFixed(2)));
      const last = visible[visible.length - 1]?.value;
      if (last != null) {
        ctx.font = '10px "JetBrains Mono", monospace';
        ctx.fillStyle = draw.color;
        ctx.fillText(`${draw.label} ${last.toFixed(4)}`, pad.left + 4, pad.top + 10);
      }
      return;
    }

    if (draw.kind === 'macd') {
      const { line, signal, hist } = draw.data;
      if (!line.length) return;
      const vLine   = line.filter(inRange);
      const vSignal = signal.filter(inRange);
      const vHist   = hist.filter(inRange);
      if (!vHist.length) return;
      const all = [...vLine.map((r) => r.value), ...vSignal.map((r) => r.value), ...vHist.map((r) => r.value)];
      const abs = Math.max(0.0001, ...all.map((v) => Math.abs(v)));
      const zeroY = pad.top + h - ((0 - (-abs)) / (2 * abs)) * h;
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.beginPath(); ctx.moveTo(pad.left, zeroY); ctx.lineTo(pad.left + w, zeroY); ctx.stroke();
      const bw = Math.max(1, barSpacing * 0.7);
      vHist.forEach((r, i) => {
        const x = xOf(r.time, i, vHist.length);
        const y = pad.top + h - ((r.value - (-abs)) / (2 * abs)) * h;
        ctx.fillStyle = r.value >= 0 ? 'rgba(0,255,136,0.6)' : 'rgba(255,51,102,0.6)';
        ctx.fillRect(x - bw / 2, Math.min(zeroY, y), bw, Math.abs(zeroY - y));
      });
      const drawSeries = (rows, color) => {
        ctx.strokeStyle = color; ctx.lineWidth = 1.2; ctx.beginPath();
        rows.forEach((r, i) => {
          const x = xOf(r.time, i, rows.length);
          const y = pad.top + h - ((r.value - (-abs)) / (2 * abs)) * h;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.stroke();
      };
      drawSeries(vLine,   '#00e5ff');
      drawSeries(vSignal, '#f7c948');
      drawYAxis(-abs, abs, [-abs, -abs / 2, 0, abs / 2, abs].map((v) => +v.toFixed(2)));
      ctx.font = '10px "JetBrains Mono", monospace';
      ctx.fillStyle = '#00e5ff';
      ctx.fillText(`${draw.label}  ${vLine[vLine.length - 1]?.value?.toFixed(3) ?? '—'}`, pad.left + 4, pad.top + 10);
      return;
    }

    if (draw.kind === 'rvol') {
      const visible = draw.rows.filter(inRange);
      const rowsWithVal = visible.filter((r) => r.value != null);
      if (!rowsWithVal.length) return;
      const rawMax = Math.max(2.5, ...rowsWithVal.map((r) => r.value));
      const yMax = Math.max(6, Math.ceil(rawMax));
      const zeroY = pad.top + h;
      const baseY = pad.top + h - (1 / yMax) * h;
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(pad.left, baseY); ctx.lineTo(pad.left + w, baseY); ctx.stroke();
      ctx.setLineDash([]);
      const bw = Math.max(1, barSpacing * 0.7);
      visible.forEach((r, i) => {
        if (r.value == null) return;
        const x = xOf(r.time, i, visible.length);
        const bar = (r.value / yMax) * h;
        const y = zeroY - bar;
        const c = r.value >= 2 ? (r.bull ? '#00ff88' : '#ff3366')
                : r.value >= 1 ? (r.bull ? 'rgba(0,255,136,0.55)' : 'rgba(255,51,102,0.55)')
                : 'rgba(148,163,184,0.4)';
        ctx.fillStyle = c;
        ctx.fillRect(x - bw / 2, y, bw, bar);
      });
      // Y-axis integer ticks
      const ticks = [];
      for (let v = 0; v <= yMax; v++) ticks.push(v);
      drawYAxis(0, yMax, ticks);
      // Live readings
      const lastRow  = rowsWithVal[rowsWithVal.length - 1];
      const lastVol  = lastRow?.volume ?? 0;
      const rvolNow  = lastRow?.value;
      const avgVol   = rvolNow > 0 ? lastVol / rvolNow : 0;
      const fmtVol = (v) => {
        if (!v) return '—';
        if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
        if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
        if (v >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
        return v.toFixed(2);
      };
      ctx.font = '10px "JetBrains Mono", monospace';
      const rvColor = rvolNow >= 2 ? '#00ff88' : rvolNow >= 1 ? '#f7c948' : '#94a3b8';
      ctx.fillStyle = rvColor;
      ctx.fillText(`${draw.label}  ${rvolNow?.toFixed(2) ?? '—'}x`, pad.left + 4, pad.top + 10);
      ctx.fillStyle = '#94a3b8';
      ctx.fillText(`Vol ${fmtVol(lastVol)}   Avg20 ${fmtVol(avgVol)}`, pad.left + 4, pad.top + 22);
      return;
    }

    if (draw.kind === 'adx') {
      const { adx: adxRows, plusDI, minusDI } = draw.data;
      if (!adxRows.length) return;
      renderLine(plusDI, '#00ff88', 0, 100);
      renderLine(minusDI, '#ff3366', 0, 100);
      renderLine(adxRows, '#f7c948', 0, 100, [25]);
      drawYAxis(0, 100, [0, 25, 50, 75, 100]);
      const last  = adxRows.filter(inRange).slice(-1)[0]?.value;
      const lastP = plusDI.filter(inRange).slice(-1)[0]?.value;
      const lastM = minusDI.filter(inRange).slice(-1)[0]?.value;
      ctx.font = '10px "JetBrains Mono", monospace';
      ctx.fillStyle = '#f7c948';
      ctx.fillText(`ADX ${last?.toFixed(1) ?? '—'}  +DI ${lastP?.toFixed(1) ?? '—'}  -DI ${lastM?.toFixed(1) ?? '—'}`, pad.left + 4, pad.top + 10);
      return;
    }
  }, [draw, size, engine, visibleRange, rangeTick, localCandles]);

  // ── Crosshair sync (G2 B.2) ─────────────────────────────────────
  // Compute the pixel-X of the shared crosshair time via the main
  // chart's timeScale. If timeToX() returns null (bar off-screen) or
  // no crosshair is set, hide the guide.
  const crossX = useMemo(() => {
    // rangeTick + size.w are intentional re-run triggers: when the
    // user zooms/pans the main chart or the pane is resized, the
    // pixel mapping of `crosshairTime` shifts and we need a fresh
    // sample from timeToX(). Referenced via `void` so ESLint's
    // exhaustive-deps rule sees the read (no side-effect).
    void rangeTick; void size.w;
    if (crosshairTime == null || !engine?.provider?.timeToX) return null;
    const x = engine.provider.timeToX(crosshairTime);
    if (x == null || !isFinite(x)) return null;
    return x;
  }, [crosshairTime, engine, rangeTick, size.w]);

  // Compute the value at the crosshair time for the "hover label"
  // shown in the top-right of the pane (e.g., "RSI 62.1").
  const crossValue = useMemo(() => {
    if (crosshairTime == null || !draw) return null;
    const findAt = (rows) => rows?.find((r) => r.time === crosshairTime)?.value;
    if (draw.kind === 'osc' || draw.kind === 'line') return findAt(draw.rows);
    if (draw.kind === 'rvol') return findAt(draw.rows);
    if (draw.kind === 'macd') return findAt(draw.data.line);
    if (draw.kind === 'adx')  return findAt(draw.data.adx);
    return null;
  }, [crosshairTime, draw]);

  // Reverse sync — when the mouse moves inside the pane, work out
  // the underlying candle time and push it up so the main chart's
  // crosshair follows too.
  const onMouseMove = (e) => {
    if (!engine?.provider?.chart || !onHoverTime || !candles?.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    // Prefer the chart's own coordinate-to-time so panes and main
    // chart use exactly the same mapping.
    let t = null;
    try { t = engine.provider.chart.timeScale().coordinateToTime(localX); } catch { /* noop */ }
    if (t == null) return;
    // Snap to the nearest candle. lightweight-charts' coordinateToTime
    // can return inter-bar values (especially on higher timeframes)
    // which would miss any exact-match candle lookup and prevent the
    // main chart from surfacing its OHLC tooltip.
    let lo = 0, hi = candles.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (candles[mid].time < t) lo = mid + 1; else hi = mid;
    }
    // Compare with the neighbour to pick the truly nearest bar.
    let idx = lo;
    if (idx > 0 && Math.abs(candles[idx - 1].time - t) < Math.abs(candles[idx].time - t)) {
      idx = idx - 1;
    }
    const bar = candles[idx];
    if (!bar) return;
    onHoverTime(bar.time);
    try {
      engine.provider.chart.setCrosshairPosition(bar.close, bar.time, engine.provider.priceSeries);
    } catch { /* noop */ }
  };
  const onMouseLeave = () => {
    if (!onHoverTime) return;
    onHoverTime(null);
    try { engine?.provider?.chart?.clearCrosshairPosition(); } catch { /* noop */ }
  };

  if (!draw) return null;
  return (
    <div ref={rootRef} data-testid={`crypto-pane-${type}`}
         className="w-full border-t border-[var(--border-default)] bg-black relative"
         style={{ height }}
         onMouseMove={onMouseMove}
         onMouseLeave={onMouseLeave}>
      <canvas ref={canvasRef} />
      {/* Synced vertical crosshair guide — driven by main-chart hover. */}
      {crossX != null && (
        <div
          data-testid={`crypto-pane-${type}-crosshair`}
          className="pointer-events-none absolute top-0 bottom-0 border-l border-dashed border-white/30"
          style={{ left: `${crossX}px` }}
        />
      )}
      {/* Hover label — shows the pane's indicator value at the crosshair. */}
      {crossValue != null && (
        <div
          data-testid={`crypto-pane-${type}-hoverval`}
          className="pointer-events-none absolute top-1 right-14 text-[10px] font-mono text-white/80 bg-black/50 px-1.5 py-0.5 rounded"
        >
          {typeof crossValue === 'number' ? crossValue.toFixed(2) : String(crossValue)}
        </div>
      )}
    </div>
  );
}
